import { useEffect, useRef, useCallback } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import {
  getFormName,
  reconcileAnalyticsIdentity,
  setAnalyticsPersonProperties,
  trackAnalytics,
} from "../pages/helpers";
import { BUTTON_ROLES, BUTTON_TYPES, EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";
import { trackPixelEvent } from "src/services/facebookPixel";
import {
  getClerkSignupMethod,
  getClerkUserEmail,
  getClerkUserNames,
  isClerkSignupSession,
} from "src/utils/clerkUserIdentity";

// Survives reloads and other tabs, so a signup is reported once per account
// rather than once per page load during the signup session.
const SIGNUP_TRACKED_USER_KEY = "signup_tracked_user_id";

export const useClerkEventTracking = () => {
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();

  const hasTrackedSignUpRef = useRef(false);
  const mutationObserverRef = useRef(null);
  const eventListenersRef = useRef(new Set());
  const inputTimeoutsRef = useRef(new Map());

  useEffect(() => {
    if (!authLoaded || !userLoaded) return;

    const signedInUser = isSignedIn ? user : null;

    const trackSignup = async () => {
      // Runs before any signup event so it is not recorded against a previous
      // account still identified in this browser.
      await reconcileAnalyticsIdentity(signedInUser?.id ?? null);
      if (!signedInUser || !isClerkSignupSession(signedInUser)) return;
      if (hasTrackedSignUpRef.current || localStorage.getItem(SIGNUP_TRACKED_USER_KEY) === signedInUser.id) return;
      // Claimed before any await so re-runs of this effect cannot report it twice.
      hasTrackedSignUpRef.current = true;
      localStorage.setItem(SIGNUP_TRACKED_USER_KEY, signedInUser.id);

      let userEmail = getClerkUserEmail(signedInUser);
      let userNames = getClerkUserNames(signedInUser);
      if (!userEmail) {
        // Clerk can deliver an OAuth signup before the email is attached.
        const reloadedUser = await signedInUser.reload().catch(() => null);
        userEmail = getClerkUserEmail(reloadedUser);
        userNames = { ...userNames, ...getClerkUserNames(reloadedUser) };
      }

      const signupMethod = getClerkSignupMethod(signedInUser);
      if (userEmail) {
        // Puts the email and name on the PostHog person before the event
        // arrives, so anything reading person properties sees them on this
        // event. identifyAnalytics() sets the name only after the event.
        await setAnalyticsPersonProperties({ email: userEmail, ...userNames });
      }
      // Event 108:
      // Carries the name itself: the Cometly webhook reads it from this event.
      trackAnalytics(EVENT_NAMES.COMPLETED_SIGNUP, {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.SIGNUP_METHOD]: signupMethod,
        [EVENT_PARAMETERS.USER_EMAIL]: userEmail || "",
        ...userNames,
      });
      trackPixelEvent("CompleteRegistration", { method: signupMethod });
    };

    trackSignup();
  }, [authLoaded, userLoaded, isSignedIn, user]);

  // Add event listener with cleanup tracking
  const addEventListenerWithCleanup = useCallback((element, event, handler) => {
    element.addEventListener(event, handler);
    const cleanup = () => element.removeEventListener(event, handler);
    eventListenersRef.current.add(cleanup);
    return cleanup;
  }, []);

  // Setup DOM event listeners for Clerk form elements
  const setupClerkFormListeners = useCallback(() => {
    // Continue/Submit button events
    const continueButtons = document.querySelectorAll(
      '.cl-formButtonPrimary, [data-localization-key="signIn.start.actionText"], [data-localization-key="signUp.start.actionText"]',
    );
    continueButtons.forEach((button) => {
      addEventListenerWithCleanup(button, "click", () => {
        var fieldLocation = getFormName();
        const buttonText = button.textContent?.trim() || "Unknown";
        // Event 109:
        trackAnalytics(
          EVENT_NAMES.CLICKED_BUTTON,
          {
            [EVENT_PARAMETERS.PAGE_NAME]: "Clerk Form: " + getPageName(),
            [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
            [EVENT_PARAMETERS.BUTTON_TEXT]: buttonText,
            [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
            // [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
            [EVENT_PARAMETERS.MODAL_NAME]: fieldLocation,
          },
          {},
        );
      });
    });

    // Google OAuth button events
    const googleButtons = document.querySelectorAll(
      '.cl-socialButtonsBlockButton[data-provider="google"], .cl-socialButtonsBlockButton:has([alt*="Google"])',
    );
    googleButtons.forEach((button) => {
      addEventListenerWithCleanup(button, "click", () => {
        var fieldLocation = getFormName();

        // Event 116:
        trackAnalytics(
          EVENT_NAMES.CLICKED_BUTTON,
          {
            [EVENT_PARAMETERS.PAGE_NAME]: "Google Auth Form: " + getPageName(),
            [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
            [EVENT_PARAMETERS.BUTTON_TEXT]: "Sign in with Google",
            [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
            // [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
            [EVENT_PARAMETERS.MODAL_NAME]: fieldLocation,
          },
          {},
        );
      });
    });

    // Generic social buttons (fallback)
    const socialButtons = document.querySelectorAll(".cl-socialButtonsBlockButton");
    socialButtons.forEach((button) => {
      // Skip if already handled by Google-specific handler
      if (button.getAttribute("data-provider") === "google" || button.querySelector('[alt*="Google"]')) {
        return;
      }

      addEventListenerWithCleanup(button, "click", () => {
        const provider = button.getAttribute("data-provider") || "Unknown";
        var fieldLocation = getFormName();
        // Event 117:
        trackAnalytics(
          EVENT_NAMES.CLICKED_BUTTON,
          {
            [EVENT_PARAMETERS.PAGE_NAME]: "Social Auth Form: " + getPageName(),
            [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
            [EVENT_PARAMETERS.BUTTON_TEXT]: `Sign in with ${provider}`,
            [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
            provider_name: provider,
            [EVENT_PARAMETERS.MODAL_NAME]: fieldLocation,
          },
          {},
        );
      });
    });
  }, [addEventListenerWithCleanup]);

  // Setup MutationObserver to detect when Clerk forms are rendered
  useEffect(() => {
    const eventListeners = eventListenersRef.current;
    const inputTimeouts = inputTimeoutsRef.current;

    const observer = new MutationObserver((mutations) => {
      let shouldSetupListeners = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if Clerk components were added
              if (
                node.classList?.contains("cl-card") ||
                node.classList?.contains("cl-formFieldInput") ||
                node.querySelector?.(".cl-card, .cl-formFieldInput")
              ) {
                shouldSetupListeners = true;
              }
            }
          });
        }
      });

      if (shouldSetupListeners) {
        // Small delay to ensure DOM is fully rendered
        setTimeout(setupClerkFormListeners, 100);
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    mutationObserverRef.current = observer;

    // Initial setup in case forms are already rendered
    setTimeout(setupClerkFormListeners, 100);

    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }

      // Clean up all event listeners
      eventListeners.forEach((cleanup) => cleanup());
      eventListeners.clear();

      // Clear any pending timeouts
      inputTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      inputTimeouts.clear();
    };
  }, [setupClerkFormListeners]);
};
